import { SimulationConfig } from '@/types';

export class FrameScheduler {
  private static pressure = 0.0; // 0.0 (smooth) to 1.0 (extremely bottlenecked)
  private static lastFrameTime = 0;
  private static frameDurations = new Float32Array(30); // Persistent zero-allocation rolling buffer
  private static ringIndex = 0;
  private static activeLODScale = 1.0;
  private static lodTriggered = false;

  /**
   * Registers a finished frame duration to adapt quality.
   * @param frameDurationMs Time spent executing update & draw for the current frame
   * @param targetFps The desired target simulation frame rate limit (defaults to 60)
   */
  public static registerFrame(frameDurationMs: number, targetFps: number = 60) {
    const budget = 1000 / (targetFps || 60);

    // Dynamic instant compensation for sudden extreme latency spike
    if (frameDurationMs > budget * 1.5) {
      this.pressure = Math.min(1.0, this.pressure + 0.2); // Sledgehammer dampening for immediate recovery
    }

    // Write to persistent rolling average buffer
    this.frameDurations[this.ringIndex] = frameDurationMs;
    this.ringIndex = (this.ringIndex + 1) % this.frameDurations.length;

    // Fast inline calculation of moving average without array allocations
    let sum = 0;
    for (let i = 0; i < this.frameDurations.length; i++) {
      sum += this.frameDurations[i];
    }
    const avgDuration = sum / this.frameDurations.length;

    // Check if the moving average exceeds 85% of our frame time budget
    const threshold = budget * 0.85;
    if (avgDuration > threshold) {
      // Accumulate scheduler pressure proportionally
      const excessRatio = (avgDuration - threshold) / budget;
      this.pressure = Math.min(1.0, this.pressure + excessRatio * 0.05);
    } else {
      // Smooth pressure decay when working comfortably under the budget
      this.pressure = Math.max(0.0, this.pressure - 0.02);
    }

    // Level of Detail Calculation (1.0 down to minimum 0.15)
    this.activeLODScale = 1.0 - (this.pressure * 0.85);
    this.lodTriggered = this.pressure > 0.2;
  }

  /**
   * Returns current quality scale multiplier (0.15 - 1.0)
   */
  public static getLODScale(): number {
    return this.activeLODScale;
  }

  /**
   * Returns pressure index (0.0 - 1.0)
   */
  public static getPressure(): number {
    return this.pressure;
  }

  /**
   * Returns whether the scheduler is currently in throttled mode
   */
  public static isLODTriggered(): boolean {
    return this.lodTriggered;
  }

  /**
   * Adjusts the number of physics sub-steps dynamically based on scheduler pressure
   */
  public static getPhysicsSubsteps(configSubsteps: number): number {
    if (this.pressure > 0.75) {
      return Math.max(1, Math.min(configSubsteps, 1)); // Throttle hard to preserve UX fluidity
    } else if (this.pressure > 0.4) {
      return Math.max(1, Math.min(configSubsteps, Math.floor(configSubsteps / 2)));
    }
    return configSubsteps;
  }

  /**
   * Wipes frame history and resets scheduler back to perfect quality
   */
  public static reset() {
    this.pressure = 0.0;
    this.activeLODScale = 1.0;
    this.lodTriggered = false;
    this.frameDurations.fill(0);
    this.ringIndex = 0;
  }
}
